import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Bell, BellOff } from 'lucide-react';

export function TelegramNotificationSettings() {
  const queryClient = useQueryClient();
  const [selectedAdmin, setSelectedAdmin] = useState<string>('');

  // Fetch notification types
  const { data: notificationTypes, isLoading: typesLoading } = useQuery({
    queryKey: ['telegram-notification-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_notification_types')
        .select('*')
        .eq('enabled', true)
        .order('display_name');
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch admin users
  const { data: admins, isLoading: adminsLoading } = useQuery({
    queryKey: ['telegram-admins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_admin_users')
        .select('*')
        .eq('is_active', true);
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch preferences for selected admin
  const { data: preferences, isLoading: prefsLoading } = useQuery({
    queryKey: ['telegram-notification-prefs', selectedAdmin],
    queryFn: async () => {
      if (!selectedAdmin) return [];
      
      const { data, error } = await supabase
        .from('telegram_admin_notification_prefs')
        .select('*')
        .eq('telegram_admin_id', selectedAdmin);
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedAdmin,
  });

  // Update preference mutation
  const updatePrefMutation = useMutation({
    mutationFn: async ({
      notificationTypeKey,
      enabled,
    }: {
      notificationTypeKey: string;
      enabled: boolean;
    }) => {
      const { data, error } = await supabase
        .from('telegram_admin_notification_prefs')
        .upsert({
          telegram_admin_id: selectedAdmin,
          notification_type_key: notificationTypeKey,
          enabled,
        }, {
          onConflict: 'telegram_admin_id,notification_type_key'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegram-notification-prefs', selectedAdmin] });
      toast.success('Notification preference updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update preference: ${error.message}`);
    },
  });

  const isLoading = typesLoading || adminsLoading || prefsLoading;

  const getPreference = (typeKey: string) => {
    return preferences?.find(p => p.notification_type_key === typeKey);
  };

  const isEnabled = (typeKey: string) => {
    const pref = getPreference(typeKey);
    return pref?.enabled ?? true; // Default to enabled
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Settings
          </CardTitle>
          <CardDescription>
            Configure which notifications each admin receives
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Admin Selector */}
          <div className="space-y-2">
            <Label>Select Admin User</Label>
            <Select value={selectedAdmin} onValueChange={setSelectedAdmin}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an admin user" />
              </SelectTrigger>
              <SelectContent>
                {admins?.map((admin) => (
                  <SelectItem key={admin.id} value={admin.id}>
                    {admin.telegram_username || admin.telegram_first_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notification Types */}
          {selectedAdmin && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Notification Types</h3>
              
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-3">
                  {notificationTypes?.map((type) => {
                    const enabled = isEnabled(type.type_key);
                    
                    return (
                      <div
                        key={type.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {enabled ? (
                              <Bell className="h-4 w-4 text-primary" />
                            ) : (
                              <BellOff className="h-4 w-4 text-muted-foreground" />
                            )}
                            <h4 className="font-medium">{type.display_name}</h4>
                            <span className="text-xs px-2 py-1 rounded-full bg-secondary">
                              {type.default_priority}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {type.description}
                          </p>
                          {type.cooldown_seconds > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Cooldown: {type.cooldown_seconds}s
                            </p>
                          )}
                        </div>
                        
                        <Switch
                          checked={enabled}
                          onCheckedChange={(checked) => {
                            updatePrefMutation.mutate({
                              notificationTypeKey: type.type_key,
                              enabled: checked,
                            });
                          }}
                          disabled={updatePrefMutation.isPending}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
